// Öffentliche Signier-Seite (/signieren/:token) — E-Signatur-Feature (DocuSign-
// Nachbau), Design-Doc docs/superpowers/specs/2026-07-24-esignatur-design.md,
// Abschnitt „Öffentliche Seite /signieren/:token". Lädt den Vorgang über die
// Edge Function `signieren` (GET ?token=…), rendert die PDF-Seiten via pdfjs-dist
// (dynamic import — identisches Lade-Muster wie SignaturEditor, damit Editor und
// Signier-Seite die Felder pixelgleich in % positionieren) und sammelt Werte/
// Unterschriften der eigenen Felder, bevor sie per POST abgeschlossen werden.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Loader2, AlertCircle, Clock, PenLine } from 'lucide-react';
import { supabaseUrl } from '../../lib/supabase/client';
import UnterschriftPad from '../../components/ui/UnterschriftPad';
import { useViewport } from '../../components/island/hooks';
import { istKompakt } from '../../components/island/island-size';
import { ladePdfjs } from '../../lib/pdf/pdfjs';

const API = `${supabaseUrl}/functions/v1/signieren`;

type FeldTyp = 'signatur' | 'text' | 'datum' | 'checkbox';

interface Feld {
  id: string;
  typ: FeldTyp;
  seite: number;
  x: number;
  y: number;
  w: number;
  h: number;
  unterzeichner: number;
  label: string;
  pflicht: boolean;
  fixiert?: boolean; // Absender-Wert, nur Anzeige — Eingabe gesperrt
}

interface UnterzeichnerInfo {
  name: string;
  status: 'offen' | 'angesehen' | 'signiert';
}

interface VorgangsDaten {
  titel: string;
  status: 'verschickt' | 'abgeschlossen' | string;
  meinIndex: number;
  dran: boolean;
  pdfUrl: string | null;
  felder: Feld[];
  unterzeichner: UnterzeichnerInfo[];
  meineWerte: Record<string, string | boolean>;
}

// #583: Legacy-Build über den gemeinsamen Lader (ältere Handy-Browser).
const importPdfjs = ladePdfjs;

// Datums-Konvention: GESPEICHERT (und ins PDF gebrannt) wird deutsches Format
// TT.MM.JJJJ — die nativen date-Inputs arbeiten intern aber mit JJJJ-MM-TT.
const deutschZuIso = (v: string): string => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(v.trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : v;
};
const isoZuDeutsch = (v: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
};

// ── Feld-Overlay ──────────────────────────────────────────────────────────
interface FeldOverlayProps {
  feld: Feld;
  eigen: boolean;
  wert: string | boolean | undefined;
  unterschriftDataUrl: string | undefined;
  onWertAendern: (feldId: string, wert: string | boolean) => void;
  onSignaturKlick: (feldId: string) => void;
  istKompaktModus: boolean;
  istAktiv: boolean;
  onMobilOeffnen: (feld: Feld) => void;
  onFeldRef: (el: HTMLElement | null) => void;
}

const FeldOverlay: React.FC<FeldOverlayProps> = ({
  feld,
  eigen,
  wert,
  unterschriftDataUrl,
  onWertAendern,
  onSignaturKlick,
  istKompaktModus,
  istAktiv,
  onMobilOeffnen,
  onFeldRef,
}) => {
  const style: React.CSSProperties = {
    left: `${feld.x}%`,
    top: `${feld.y}%`,
    width: `${feld.w}%`,
    height: `${feld.h}%`,
  };
  const ringKlasse = istAktiv ? 'ring-2 ring-[#c4a77d] ring-offset-2' : '';

  // Vom Absender fixierte Felder: Wert nur anzeigen, keine Eingabe — der Server
  // erzwingt den Absender-Wert beim Abschluss ohnehin (Manipulationsschutz).
  if (feld.fixiert && feld.typ !== 'signatur') {
    if (feld.typ === 'checkbox') {
      return (
        <div
          className={`absolute flex items-center justify-center rounded-md border-2 ${
            wert === true ? 'border-gray-400 bg-gray-400' : 'border-gray-300 bg-gray-50'
          }`}
          style={style}
          aria-label={feld.label}
        >
          {wert === true && <Check size={Math.max(10, feld.h)} className="text-white" strokeWidth={3} />}
        </div>
      );
    }
    return (
      <div className="absolute flex items-center px-1.5 rounded-md border border-gray-300 bg-gray-50/90 overflow-hidden" style={style}>
        <span className="text-[10px] font-semibold text-gray-700 truncate">{typeof wert === 'string' ? wert : ''}</span>
      </div>
    );
  }

  if (!eigen) {
    return (
      <div
        className="absolute flex items-center px-1.5 rounded-md border border-dashed border-gray-300 bg-gray-50/70 overflow-hidden"
        style={style}
      >
        <span className="text-[8px] font-bold uppercase tracking-wide text-gray-400 truncate">
          {feld.label} <span className="italic normal-case">(anderer Unterzeichner)</span>
        </span>
      </div>
    );
  }

  if (feld.typ === 'signatur') {
    return (
      <button
        ref={onFeldRef as unknown as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={() => onSignaturKlick(feld.id)}
        className={`absolute flex items-center justify-center rounded-md border-2 border-dashed border-[#c4a77d] bg-[#c4a77d]/10 hover:bg-[#c4a77d]/20 transition-colors overflow-hidden ${ringKlasse}`}
        style={style}
      >
        {unterschriftDataUrl ? (
          <img src={unterschriftDataUrl} alt="Unterschrift" className="max-w-full max-h-full object-contain" />
        ) : (
          <span className="flex items-center gap-1 text-[9px] font-black uppercase tracking-wide text-[#a8875f] px-1">
            <PenLine size={10} /> Hier unterschreiben
          </span>
        )}
      </button>
    );
  }

  if (feld.typ === 'checkbox') {
    const angehakt = wert === true;
    return (
      <button
        ref={onFeldRef as unknown as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={() => onWertAendern(feld.id, !angehakt)}
        className={`absolute flex items-center justify-center rounded-md border-2 transition-colors ${
          angehakt ? 'border-[#c4a77d] bg-[#c4a77d]' : 'border-[#c4a77d]/60 bg-white hover:bg-[#c4a77d]/10'
        } ${ringKlasse}`}
        style={style}
        aria-label={feld.label}
      >
        {angehakt && <Check size={Math.max(10, feld.h)} className="text-white" strokeWidth={3} />}
      </button>
    );
  }

  // text | datum
  if (istKompaktModus) {
    const hatWert = typeof wert === 'string' && wert.trim() !== '';
    return (
      <button
        ref={onFeldRef as unknown as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={() => onMobilOeffnen(feld)}
        className={`absolute flex items-center px-1.5 rounded-md border-2 border-[#c4a77d] bg-white/90 text-[10px] font-semibold text-left overflow-hidden ${
          hatWert ? 'text-gray-800' : 'text-gray-400'
        } ${ringKlasse}`}
        style={style}
      >
        <span className="truncate">{hatWert ? (wert as string) : feld.label}</span>
      </button>
    );
  }

  return (
    <input
      ref={onFeldRef as unknown as React.Ref<HTMLInputElement>}
      type={feld.typ === 'datum' ? 'date' : 'text'}
      value={typeof wert === 'string' ? (feld.typ === 'datum' ? deutschZuIso(wert) : wert) : ''}
      onChange={(e) => onWertAendern(feld.id, feld.typ === 'datum' ? isoZuDeutsch(e.target.value) : e.target.value)}
      placeholder={feld.label}
      className="absolute px-1.5 rounded-md border-2 border-[#c4a77d] bg-white/90 text-[10px] font-semibold text-gray-800 focus:outline-none focus:bg-white [color-scheme:light]"
      style={style}
    />
  );
};

interface SignierenSeiteProps {
  /** Im Dashboard eingebettet: Token direkt übergeben (kein Mailversand nötig). */
  token?: string;
  /** Nach erfolgreichem Abschluss — die Liste dahinter lädt dann neu. */
  onFertig?: () => void;
}

const SignierenSeite: React.FC<SignierenSeiteProps> = (props) => {
  // Im Dashboard wird dieselbe Ansicht mit übergebenem Token eingebettet („selbst
  // unterschreiben", ohne Einladungsmail) — sonst kommt er wie bisher aus der URL.
  const token = props.token || window.location.pathname.split('/')[2] || '';

  const [laden, setLaden] = useState(true);
  const [fehlerLaden, setFehlerLaden] = useState<string | null>(null);
  const [daten, setDaten] = useState<VorgangsDaten | null>(null);

  const [werte, setWerte] = useState<Record<string, string | boolean>>({});
  const [unterschriften, setUnterschriften] = useState<Record<string, string>>({});
  const [signaturFeldOffen, setSignaturFeldOffen] = useState<string | null>(null);

  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [seitenGeladen, setSeitenGeladen] = useState(false);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);

  const [sende, setSende] = useState(false);
  const [fehlerSenden, setFehlerSenden] = useState<string | null>(null);
  const [fehlendeFelder, setFehlendeFelder] = useState<string[]>([]);
  const [erfolg, setErfolg] = useState<{ fertig: boolean } | null>(null);

  // ── Kompakt-Erkennung (schmal ODER kurz, wie die Pille) + aktives Feld
  // für Bottom-Sheet-Eingabe ──────────────────────────────────────────────
  // Bewusst KEINE eigene Breiten-Abfrage: istKompakt ist die etablierte
  // Vollbild-Entscheidung der App (components/island/island-size.ts) und
  // erkennt auch das Querformat (844×390 — breit, aber ohne Höhe für zwei
  // fixierte Leisten samt Abschließen-Knopf).
  const viewport = useViewport();
  const istKompaktModus = istKompakt(viewport.width, viewport.height);
  const [mobilAktivFeldId, setMobilAktivFeldId] = useState<string | null>(null);

  const pdfDocRef = useRef<any>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const feldRefs = useRef<Record<string, HTMLElement | null>>({});

  // ── Vorgang laden ──────────────────────────────────────────────────────
  const ladeVorgang = useCallback(async () => {
    if (!token) {
      setFehlerLaden('Dieser Link ist ungültig.');
      setLaden(false);
      return;
    }
    try {
      const res = await fetch(`${API}?token=${encodeURIComponent(token)}`);
      const d = await res.json();
      if (d.ok) {
        setDaten({
          titel: d.titel,
          status: d.status,
          meinIndex: d.meinIndex,
          dran: d.dran,
          pdfUrl: d.pdfUrl ?? null,
          felder: d.felder || [],
          unterzeichner: d.unterzeichner || [],
          meineWerte: d.meineWerte || {},
        });
        // Vorbelegung des Absenders als Startwerte — eigene Eingaben gewinnen
        setWerte({ ...(d.vorbelegung || {}), ...(d.meineWerte || {}) });
        setFehlerLaden(null);
      } else {
        setDaten(null);
        setFehlerLaden(d.fehler || 'Dieser Link ist ungültig.');
      }
    } catch {
      setFehlerLaden('Verbindungsfehler — bitte laden Sie die Seite neu.');
    } finally {
      setLaden(false);
    }
  }, [token]);

  useEffect(() => {
    ladeVorgang();
  }, [ladeVorgang]);

  // ── PDF laden (identisches Muster wie SignaturEditor) ─────────────────
  useEffect(() => {
    if (!daten?.pdfUrl) return;
    let abgebrochen = false;
    (async () => {
      try {
        const res = await fetch(daten.pdfUrl!);
        const bytes = await res.arrayBuffer();
        const pdfjs = await importPdfjs();
        const pdfDoc = await pdfjs.getDocument({ data: bytes }).promise;
        if (abgebrochen) return;
        pdfDocRef.current = pdfDoc;
        setPdfNumPages(pdfDoc.numPages);
      } catch {
        if (!abgebrochen) setLadeFehler('Das Dokument konnte nicht geladen werden.');
      }
    })();
    return () => { abgebrochen = true; };
  }, [daten?.pdfUrl]);

  useEffect(() => {
    if (pdfNumPages <= 0 || !pdfDocRef.current) return;
    let abgebrochen = false;
    (async () => {
      setSeitenGeladen(false);
      const pdfDoc = pdfDocRef.current;
      try {
        for (let i = 1; i <= pdfDoc.numPages; i++) {
          const page = await pdfDoc.getPage(i);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = canvasRefs.current[i - 1];
          if (canvas) {
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const ctx = canvas.getContext('2d');
            if (ctx) await page.render({ canvasContext: ctx, viewport } as any).promise;
          }
        }
      } catch {
        // #583: früher unbehandelt → leere weiße Seiten ohne Hinweis
        if (!abgebrochen) setLadeFehler('Das Dokument konnte auf diesem Gerät nicht angezeigt werden. Bitte versuchen Sie es mit einem aktuellen Browser oder am Computer.');
        return;
      }
      if (abgebrochen) return;
      setSeitenGeladen(true);
    })();
    return () => { abgebrochen = true; };
  }, [pdfNumPages]);

  // ── Eigene Pflichtfelder & Fortschritt ─────────────────────────────────
  const eigenePflichtfelder = (daten?.felder || []).filter(
    (f) => f.unterzeichner === daten?.meinIndex && f.pflicht,
  );
  const istAusgefuellt = (f: Feld): boolean => {
    if (f.typ === 'signatur') return !!unterschriften[f.id];
    if (f.typ === 'checkbox') return werte[f.id] === true;
    return typeof werte[f.id] === 'string' && (werte[f.id] as string).trim() !== '';
  };
  const anzahlAusgefuellt = eigenePflichtfelder.filter(istAusgefuellt).length;
  const alleAusgefuellt = anzahlAusgefuellt === eigenePflichtfelder.length;

  // Dokument-Reihenfolge (Seite, dann vertikale Position) für die
  // „Weiter →"-Navigation der mobilen Eingabeleiste.
  const eigenePflichtfelderSortiert = [...eigenePflichtfelder].sort(
    (a, b) => a.seite - b.seite || a.y - b.y,
  );
  const naechstesUnausgefuelltesFeld = (nachFeldId: string): Feld | null => {
    const idx = eigenePflichtfelderSortiert.findIndex((f) => f.id === nachFeldId);
    for (let i = idx + 1; i < eigenePflichtfelderSortiert.length; i++) {
      if (!istAusgefuellt(eigenePflichtfelderSortiert[i])) return eigenePflichtfelderSortiert[i];
    }
    return null;
  };

  const setWert = (feldId: string, wert: string | boolean) => {
    setWerte((prev) => ({ ...prev, [feldId]: wert }));
  };

  // ── Mobile Bottom-Sheet-Eingabe: Feld öffnen / Weiter / Fertig ─────────
  const feldMobilOeffnen = (feld: Feld) => {
    setMobilAktivFeldId(feld.id);
    if (feld.typ === 'signatur') setSignaturFeldOffen(feld.id);
  };
  const mobilEingabeFertig = () => setMobilAktivFeldId(null);
  const mobilEingabeWeiter = () => {
    if (!mobilAktivFeldId) return;
    const naechstes = naechstesUnausgefuelltesFeld(mobilAktivFeldId);
    if (!naechstes) {
      setMobilAktivFeldId(null);
      return;
    }
    setMobilAktivFeldId(naechstes.id);
    if (naechstes.typ === 'signatur') setSignaturFeldOffen(naechstes.id);
    feldRefs.current[naechstes.id]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const uebernehmeUnterschrift = (dataUrl: string) => {
    if (!signaturFeldOffen) return;
    setUnterschriften((prev) => ({ ...prev, [signaturFeldOffen]: dataUrl }));
    setSignaturFeldOffen(null);
  };

  // Aktives Feld der mobilen Eingabeleiste — nur für text/datum gezeigt
  // (Checkbox togglet direkt, Signatur öffnet das Zeichen-Modal).
  const mobilAktivFeld = mobilAktivFeldId
    ? (daten?.felder || []).find((f) => f.id === mobilAktivFeldId) || null
    : null;
  const zeigeEingabeLeiste =
    istKompaktModus && !!mobilAktivFeld && (mobilAktivFeld.typ === 'text' || mobilAktivFeld.typ === 'datum') && !mobilAktivFeld.fixiert;

  // ── Abschließen ─────────────────────────────────────────────────────────
  const abschliessen = async () => {
    if (!alleAusgefuellt || sende) return;
    setSende(true);
    setFehlerSenden(null);
    setFehlendeFelder([]);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, werte, unterschriften }),
      });
      const d = await res.json();
      if (d.ok) {
        setErfolg({ fertig: !!d.fertig });
      } else {
        setFehlerSenden(d.fehler || 'Das hat leider nicht geklappt.');
        setFehlendeFelder(d.fehlendeFelder || []);
      }
    } catch {
      setFehlerSenden('Verbindungsfehler — bitte erneut versuchen.');
    } finally {
      setSende(false);
    }
  };

  // ── Zustände: laden / Fehler / nicht dran / abgeschlossen ──────────────
  if (laden) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1d24] p-6">
        <div className="w-full max-w-md bg-white/[0.06] border border-white/10 rounded-[2rem] backdrop-blur-xl p-10 text-center">
          <p className="text-[10px] font-black text-[#c4a77d] uppercase tracking-[0.3em] mb-8">
            Immobilienmanagement Schwede
          </p>
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 size={28} className="text-[#c4a77d] animate-spin" />
            <p className="text-xs font-semibold text-white/40 uppercase tracking-widest">Dokument wird geladen…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!daten) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1d24] p-6">
        <div className="w-full max-w-md bg-white/[0.06] border border-white/10 rounded-[2rem] backdrop-blur-xl p-10 text-center">
          <p className="text-[10px] font-black text-[#c4a77d] uppercase tracking-[0.3em] mb-8">
            Immobilienmanagement Schwede
          </p>
          <div className="py-4">
            <AlertCircle size={36} className="text-white/30 mx-auto mb-5" />
            <h1 className="text-lg font-black text-white mb-2">Link ungültig</h1>
            <p className="text-sm text-white/60 leading-relaxed">{fehlerLaden}</p>
          </div>
        </div>
      </div>
    );
  }

  if (daten.status === 'abgeschlossen') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1d24] p-6">
        <div className="w-full max-w-md bg-white/[0.06] border border-white/10 rounded-[2rem] backdrop-blur-xl p-10 text-center">
          <p className="text-[10px] font-black text-[#c4a77d] uppercase tracking-[0.3em] mb-8">
            Immobilienmanagement Schwede
          </p>
          <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center bg-emerald-500/20 text-emerald-400">
            <Check size={30} />
          </div>
          <h1 className="text-lg font-black text-white mb-3">Bereits abgeschlossen</h1>
          <p className="text-sm text-white/70 leading-relaxed">
            Das fertige Dokument wurde per E-Mail verschickt.
          </p>
        </div>
      </div>
    );
  }

  if (!daten.dran) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1d24] p-6">
        <div className="w-full max-w-md bg-white/[0.06] border border-white/10 rounded-[2rem] backdrop-blur-xl p-10 text-center">
          <p className="text-[10px] font-black text-[#c4a77d] uppercase tracking-[0.3em] mb-8">
            Immobilienmanagement Schwede
          </p>
          <Clock size={36} className="text-white/30 mx-auto mb-5" />
          <h1 className="text-lg font-black text-white mb-3">{daten.titel}</h1>
          <p className="text-sm text-white/70 leading-relaxed">
            Das Dokument liegt gerade bei einem anderen Unterzeichner — Sie erhalten eine E-Mail, sobald Sie an der
            Reihe sind.
          </p>
        </div>
      </div>
    );
  }

  if (erfolg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1d24] p-6">
        <div className="w-full max-w-md bg-white/[0.06] border border-white/10 rounded-[2rem] backdrop-blur-xl p-10 text-center">
          <p className="text-[10px] font-black text-[#c4a77d] uppercase tracking-[0.3em] mb-8">
            Immobilienmanagement Schwede
          </p>
          <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center bg-emerald-500/20 text-emerald-400">
            <Check size={30} />
          </div>
          <h1 className="text-lg font-black text-white mb-3">Vielen Dank!</h1>
          <p className="text-sm text-white/70 leading-relaxed">
            {erfolg.fertig
              ? 'Alle Unterschriften sind da — Sie erhalten das fertige Dokument in Kürze per E-Mail.'
              : 'Sobald alle unterschrieben haben, erhalten Sie das fertige Dokument per E-Mail.'}
          </p>
          {props.onFertig && (
            <button
              type="button"
              onClick={props.onFertig}
              className="mt-8 px-5 py-2.5 rounded-xl bg-[#c4a77d] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#b3966c] transition-colors"
            >
              Zurück zur Übersicht
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Signieren-Ansicht ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#1a1d24]">
      {/* Kopfleiste */}
      <div className="sticky top-0 z-40 bg-[#1a1d24]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-2 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] font-black text-[#c4a77d] uppercase tracking-[0.3em]">
              Immobilienmanagement Schwede
            </p>
            <h1 className="text-sm font-black text-white truncate mt-0.5">{daten.titel}</h1>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <span className="text-[10px] font-bold text-white/50 uppercase tracking-widest">
              {anzahlAusgefuellt}/{eigenePflichtfelder.length} Feldern ausgefüllt
            </span>
            {/* Im Kompakt-Modus übernimmt die fixe Bottom-Bar den Abschließen-Button */}
            {!istKompaktModus && (
              <button
                type="button"
                onClick={abschliessen}
                disabled={!alleAusgefuellt || sende}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-white bg-[#c4a77d] hover:bg-[#b3966a] transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {sende ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Abschließen
              </button>
            )}
          </div>
        </div>
        {fehlerSenden && (
          <div className="max-w-4xl mx-auto px-2 sm:px-6 pb-4">
            <p className="text-xs font-semibold text-red-400">
              {fehlerSenden}
              {fehlendeFelder.length > 0 && ` (${fehlendeFelder.join(', ')})`}
            </p>
          </div>
        )}
      </div>

      {/* PDF-Seiten */}
      <div className={`max-w-4xl mx-auto px-2 sm:px-6 py-6 sm:py-10 ${istKompaktModus ? 'pb-28 kurz:pb-20' : ''}`}>
        {ladeFehler ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-red-400 bg-white/5 rounded-[1.5rem] border border-white/10">
            <AlertCircle size={32} />
            <p className="text-xs font-bold uppercase tracking-widest">{ladeFehler}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-8">
            {Array.from({ length: pdfNumPages }).map((_, i) => {
              const seiteNr = i + 1;
              return (
                <div
                  key={i}
                  className="relative bg-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] w-full max-w-4xl"
                >
                  <canvas ref={(el) => { canvasRefs.current[i] = el; }} className="block w-full h-auto" />
                  {seitenGeladen && daten.felder.filter((f) => f.seite === seiteNr).map((f) => (
                    <FeldOverlay
                      key={f.id}
                      feld={f}
                      eigen={f.unterzeichner === daten.meinIndex}
                      wert={werte[f.id]}
                      unterschriftDataUrl={unterschriften[f.id]}
                      onWertAendern={setWert}
                      onSignaturKlick={setSignaturFeldOffen}
                      istKompaktModus={istKompaktModus}
                      istAktiv={mobilAktivFeldId === f.id}
                      onMobilOeffnen={feldMobilOeffnen}
                      onFeldRef={(el) => { feldRefs.current[f.id] = el; }}
                    />
                  ))}
                </div>
              );
            })}
            {!seitenGeladen && pdfNumPages === 0 && !ladeFehler && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-[#c4a77d]" size={32} />
              </div>
            )}
          </div>
        )}
      </div>

      {signaturFeldOffen && (
        <UnterschriftPad
          onUebernehmen={uebernehmeUnterschrift}
          onSchliessen={() => setSignaturFeldOffen(null)}
          istMobil={istKompaktModus}
          titel="Unterschreiben"
        />
      )}

      {/* Mobile Bottom-Sheet-Eingabe für eigene Text-/Datumsfelder */}
      {zeigeEingabeLeiste && mobilAktivFeld && (
        <div className="fixed bottom-0 inset-x-0 z-50 bg-[#1a1d24] border-t border-white/10 shadow-[0_-10px_30px_-10px_rgba(0,0,0,0.6)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
          {/* Unter `kurz:` (Querformat-Handy, max. 519px hoch) deutlich flacher —
              sonst frisst die Leiste allein schon rund die Hälfte des Screens. */}
          <div className="px-4 pt-3 pb-4 kurz:px-3 kurz:pt-2 kurz:pb-2">
            <label className="block text-[9px] font-black text-[#c4a77d] uppercase tracking-widest mb-2 kurz:hidden">
              {mobilAktivFeld.label}
            </label>
            <input
              autoFocus
              type={mobilAktivFeld.typ === 'datum' ? 'date' : 'text'}
              value={typeof werte[mobilAktivFeld.id] === 'string'
                ? (mobilAktivFeld.typ === 'datum' ? deutschZuIso(werte[mobilAktivFeld.id] as string) : (werte[mobilAktivFeld.id] as string))
                : ''}
              onChange={(e) => setWert(mobilAktivFeld.id, mobilAktivFeld.typ === 'datum' ? isoZuDeutsch(e.target.value) : e.target.value)}
              placeholder={mobilAktivFeld.label}
              className="w-full px-4 py-3 mb-3 rounded-xl border-2 border-[#c4a77d] bg-white text-base font-semibold text-gray-800 focus:outline-none [color-scheme:light] kurz:py-2 kurz:mb-2"
            />
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={mobilEingabeFertig}
                className="flex-1 min-h-[44px] rounded-xl text-xs font-black uppercase tracking-widest text-white bg-[#c4a77d] active:scale-95 transition-all kurz:min-h-[36px]"
              >
                Fertig
              </button>
              <button
                type="button"
                onClick={mobilEingabeWeiter}
                className="flex-1 min-h-[44px] rounded-xl text-xs font-black uppercase tracking-widest text-white border border-white/20 active:scale-95 transition-all kurz:min-h-[36px]"
              >
                Weiter →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixe Abschließen-Bar im Kompakt-Modus (nicht während die Eingabeleiste offen ist) */}
      {istKompaktModus && !zeigeEingabeLeiste && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-[#1a1d24]/95 backdrop-blur-xl border-t border-white/10 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] kurz:pt-2 kurz:pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
          <button
            type="button"
            onClick={abschliessen}
            disabled={!alleAusgefuellt || sende}
            className="w-full h-[52px] rounded-2xl flex items-center justify-center gap-2 text-xs font-black uppercase tracking-widest text-white bg-[#c4a77d] active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed kurz:h-11"
          >
            {sende ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            Abschließen
            <span className="text-[10px] font-bold normal-case opacity-80">
              ({anzahlAusgefuellt} von {eigenePflichtfelder.length} Feldern)
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SignierenSeite;
